import { Col, Row } from "reactstrap";

import CommonModal from "@components/ui-components/modal";
import EmailThread from "./email-thread";
import Flex from "@components/ui-components/flex";
import { FormProvider } from "react-hook-form";
import { LoaderOverlay } from "@components/spinner";
import LoadingButton from "@components/ui-components/loading-button";
import { NoDataComponent } from "@components/no-data-component";
import UpsertEmail from "./upsert";
import classNames from "classnames";
import { extractName } from "./misc/constants";
import size from "lodash/size";
import useDateHandler from "@hooks/use-date-handler";
import useEmail from "./use-email";
import { useTranslation } from "react-i18next";
import useUpsertEmail from "./upsert/use-upsert-email";

const Email = () => {

    const {
        loading,
        emails:{ data, token },
        handleActiveEmail,
        activeEmail,
        getAllEmails,
        getEmailThread
    } = useEmail();
    const {
        toggleModal,
        methods,
        isOpen,
        onSubmit,
        attachments,
        handleFileUpload,
        removeUploadedFile,
        loading: upsertLoading
    } = useUpsertEmail({ 
        getAllEmails, activeEmail });

    const { dateFormatter } = useDateHandler();

    const availableEmails = size(data);
    const { t } = useTranslation();
    return (
        <LoaderOverlay active={loading}>
            {availableEmails ? (
                <div
                    style={{ maxHeight: "calc(95vh - 1rem)" }}
                    className="w-100 overflow-hidden">
                    <div className="w-100 border-bottom p-75 mb-1">
                        <Flex justify="between" align="center">
                            <h3>{t("Emails")}</h3>
                            <LoadingButton onClick={toggleModal} text="Send Email" />
                        </Flex>
                    </div>
                    <Row className="w-100" style={{ height: "calc(80vh - 1rem)" }}>
                        <Col
                            lg={4}
                            className="mt-1 border-end overflow-y-auto"
                            style={{ maxHeight: "100%" }}>
                            {data.map((email) => {
                                return (
                                    <Flex
                                        justify="between"
                                        align="start"
                                        column
                                        key={email.id}
                                        onClick={() => handleActiveEmail(email)}
                                        className={classNames(
                                            "w-100 p-50 border mt-50 rounded cursor-pointer",
                                            {
                                                "border-primary":
                                                    activeEmail?.message_id === email.message_id
                                            }
                                        )}>
                                        {email.sender && (
                                            <div className="d-flex align-items-baseline">
                                                <i className="mdi mdi-email me-25"></i>
                                                <span >{extractName(email.sender)?.name || email.sender}</span>
                                            </div>
                                        )}
                                        <div className="w-100 p-25">
                                            <p className="font-medium-3 text-nowrap text-truncate overflow-hidden">
                                                {email.subject || "No Subject"}
                                            </p>
                                            <span className="font-small-4 text-muted">
                                                {dateFormatter({
                                                    date    : email.date,
                                                    formatAs: "MMM DD, YYYY"
                                                })}
                                            </span>
                                        </div>
                                    </Flex>
                                );
                            })}
                            {size(token) ? (
                                <LoadingButton text="See More" onClick={getAllEmails} />
                            ) : null}
                        </Col>
                        <Col
                            lg={8}
                            className="rounded overflow-x-hidden overflow-y-auto"
                            style={{ maxHeight: "100%" }}>
                            {activeEmail && (
                                <div className="p-50">
                                    <Flex justify="between" align="start" >
                                        <h3>{activeEmail["subject"]}</h3>
                                        <p style={{ minWidth: "100px" }} className="font-small-4 text-muted">
                                            {dateFormatter({
                                                date    : activeEmail["date"],
                                                formatAs: "MMM DD YYYY, HH:MM"
                                            })}
                                        </p>
                                    </Flex>
                                    <EmailThread thread={getEmailThread()} />
                                    <FormProvider {...methods}>
                                        <UpsertEmail
                                            isReply
                                            handleFileUpload={handleFileUpload}
                                            attachments={attachments}
                                            removeUploadedFile={removeUploadedFile} />
                                    </FormProvider>
                                    <LoadingButton
                                        className="align-self-end"
                                        text="Reply"
                                        onClick={() => onSubmit(true)} />
                                </div>
                            )}
                        </Col>
                    </Row>
                    <CommonModal
                        type="large"
                        isOpen={isOpen}
                        handleModal={toggleModal}
                        title={"Send Email"}
                        loading={loading}
                        confirmButton={{
                            txt     : "Submit",
                            color   : "primary",
                            onClick : onSubmit,
                            disabled: upsertLoading
                        }}
                        isDirty={methods.formState.isDirty}
                        body={
                            <FormProvider {...methods}>
                                <UpsertEmail
                                    handleFileUpload={handleFileUpload}
                                    attachments={attachments}
                                    removeUploadedFile={removeUploadedFile} />
                            </FormProvider>
                        } />
                </div>
            ) : (
                <NoDataComponent />
            )}
        </LoaderOverlay>
    );
};

export default Email;